import Heading from '@atlaskit/heading';
import { Stack, Box, Inline, Text, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { useDatabaseContent } from '@databases/database-state';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import AutoreviewIllustration from '@databases/icons/AutoreviewIllustration';
import { ViewChangeTypes } from '@databases/utils/database/view/view';
import React, { FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../../hooks/useView';
import { messages } from '../../i18n';

const TableLayoutOptions: FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const { view, setViewChange } = useView();
  const client = useAnalytics();
  const { contentId } = useDatabaseContent();
  const isNumberedEntriesEnabled = useBooleanFeatureFlag(
    'databases-frontend-numbered-entries'
  );

  const handleToggleShowNumberedEntries = useCallback(() => {
    if (!view.settings.layout.showNumberedEntries && contentId) {
      client.sendNumberedEntriesToggledEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'TableLayoutOptions',
        value: true,
      });
    }

    setViewChange({
      type: ViewChangeTypes.LAYOUT_NUMBERED_ENTRIES_SET,
      value: !view.settings.layout.showNumberedEntries,
    });
  }, [client, contentId, setViewChange, view.settings.layout.showNumberedEntries]);

  return isNumberedEntriesEnabled ? (
    <Stack>
      <Inline spread="space-between" alignBlock="center">
        <Text maxLines={1}>
          {formatMessage(messages['view-settings.layout.number-entries'])}
        </Text>
        <Box>
          <Toggle
            testId="numbered-entries-toggle"
            isChecked={view.settings.layout.showNumberedEntries}
            onChange={handleToggleShowNumberedEntries}
          />
        </Box>
      </Inline>
    </Stack>
  ) : (
    <Stack alignBlock="center" alignInline="center">
      <Box
        xcss={xcss({
          width: '183px',
          textAlign: 'center',
        })}
      >
        <AutoreviewIllustration label="" />
        <Heading as="h4" size="small">
          {formatMessage(messages['view-settings.layout.table-options.empty-state-text'])}
        </Heading>
      </Box>
    </Stack>
  );
};

export default TableLayoutOptions;
